<script setup>
import { ref, reactive, watch, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import PromptSectionAccordion from './PromptSectionAccordion.vue';
import PersonalityConfigForm from './PersonalityConfigForm.vue';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close', 'update']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const activeTab = ref('prompts'); // 'prompts' or 'personality'
const isLoading = ref(false);

// Prompt Sections State
const promptSections = reactive({
  jobscope: '',
  handlingFlow: '',
  overview: '',
  productHighlights: '',
});

// Personality Config State
const personalityConfig = reactive({
  gender: 'neutral',
  responseStyle: 'adaptive',
  aiModel: 'default',
  country: '',
  preferredLanguage: 'en',
});

// Prompt Section examples
const promptExamples = {
  jobscope:
    '- Only follow the identity assigned.\n- Stay within your defined role and responsibilities.',
  handlingFlow:
    '- Read through the product information provided to you and reply customer with accurate answers.\n- Always verify information before responding.',
  overview:
    '- Wellous provide various products for different customers with different needs.\n- Focus on understanding customer requirements first.',
  productHighlights:
    '- Positive reviews and feedbacks from customers.\n- Highlight key benefits and unique selling points.',
};

// Prompt Section tooltips
const promptTooltips = {
  jobscope:
    "Define the scope and boundaries of the AI assistant's responsibilities.",
  handlingFlow:
    'Specify how the AI should handle customer inquiries and process information.',
  overview:
    'Provide a general overview of your company, products, or services.',
  productHighlights:
    'Highlight key features, benefits, and selling points of your products.',
};

// Initialize state from assistant
const initializeState = () => {
  if (props.assistant?.config) {
    const config = props.assistant.config;

    // Prompt sections
    promptSections.jobscope = config.prompt_jobscope || '';
    promptSections.handlingFlow = config.prompt_handling_flow || '';
    promptSections.overview = config.prompt_overview || '';
    promptSections.productHighlights = config.prompt_product_highlights || '';

    // Personality config
    if (config.personality) {
      Object.assign(personalityConfig, config.personality);
    }
  }
};

// Watch for assistant changes
watch(
  () => props.assistant,
  () => {
    initializeState();
  },
  { immediate: true, deep: true }
);

// Build payload for update
const buildPayload = () => {
  return {
    config: {
      ...props.assistant.config,
      prompt_jobscope: promptSections.jobscope,
      prompt_handling_flow: promptSections.handlingFlow,
      prompt_overview: promptSections.overview,
      prompt_product_highlights: promptSections.productHighlights,
      personality: { ...personalityConfig },
    },
  };
};

// Handle save
const handleSave = async () => {
  isLoading.value = true;

  try {
    const payload = buildPayload();
    await store.dispatch('ailabAssistants/update', {
      id: props.assistant.id,
      ...payload,
    });

    useAlert(t('AILAB.ASSISTANTS.EDIT.SUCCESS_MESSAGE'));
    emit('update', payload);
    dialogRef.value.close();
  } catch (error) {
    const errorMessage =
      error?.message || t('AILAB.ASSISTANTS.EDIT.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    isLoading.value = false;
  }
};

// Expose dialog methods
const open = () => {
  initializeState();
  nextTick(() => {
    dialogRef.value?.open();
  });
};

const close = () => {
  dialogRef.value?.close();
  emit('close');
};

defineExpose({ open, close, dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    title="Edit Assistant"
    size="lg"
    @close="emit('close')"
  >
    <template #content>
      <div
        class="flex flex-col gap-6 min-h-[500px] max-h-[70vh] overflow-y-auto"
      >
        <!-- Tab Switcher -->
        <div class="flex gap-2 p-1 bg-n-slate-2 rounded-xl">
          <button
            class="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg transition-all"
            :class="
              activeTab === 'prompts'
                ? 'bg-white dark:bg-n-slate-1 text-n-slate-12 shadow-sm'
                : 'text-n-slate-11 hover:text-n-slate-12'
            "
            @click="activeTab = 'prompts'"
          >
            <span class="flex items-center justify-center gap-2">
              <span class="i-lucide-message-square w-4 h-4" />
              Prompt Sections
            </span>
          </button>
          <button
            class="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg transition-all"
            :class="
              activeTab === 'personality'
                ? 'bg-white dark:bg-n-slate-1 text-n-slate-12 shadow-sm'
                : 'text-n-slate-11 hover:text-n-slate-12'
            "
            @click="activeTab = 'personality'"
          >
            <span class="flex items-center justify-center gap-2">
              <span class="i-lucide-user-cog w-4 h-4" />
              AI Personality
            </span>
          </button>
        </div>

        <!-- Prompts Tab Content -->
        <div v-show="activeTab === 'prompts'" class="flex flex-col gap-4">
          <PromptSectionAccordion
            v-model="promptSections.jobscope"
            title="JOBSCOPE"
            :example="promptExamples.jobscope"
            :tooltip="promptTooltips.jobscope"
            placeholder="Enter additional instructions for the AI's job scope..."
            is-open
          />

          <PromptSectionAccordion
            v-model="promptSections.handlingFlow"
            title="HANDLING FLOW"
            :example="promptExamples.handlingFlow"
            :tooltip="promptTooltips.handlingFlow"
            placeholder="Enter instructions for how the AI should handle inquiries..."
          />

          <PromptSectionAccordion
            v-model="promptSections.overview"
            title="OVERVIEW"
            :example="promptExamples.overview"
            :tooltip="promptTooltips.overview"
            placeholder="Enter an overview of your company or products..."
          />

          <PromptSectionAccordion
            v-model="promptSections.productHighlights"
            title="PRODUCT HIGHLIGHTS"
            :example="promptExamples.productHighlights"
            :tooltip="promptTooltips.productHighlights"
            placeholder="Enter key product highlights and benefits..."
          />
        </div>

        <!-- Personality Tab Content -->
        <div v-show="activeTab === 'personality'" class="py-2">
          <PersonalityConfigForm v-model="personalityConfig" />
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex justify-end gap-3">
        <Button
          color="slate"
          variant="ghost"
          :label="t('AILAB.ASSISTANTS.FORM.CANCEL')"
          @click="close"
        />
        <Button
          color="primary"
          :loading="isLoading"
          label="Save"
          class="!bg-woot-500 hover:!bg-woot-600"
          @click="handleSave"
        />
      </div>
    </template>
  </Dialog>
</template>
